import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import type { CustomerNote, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { dateTime } from "@/modules/contacts/customers/format";
import { useCustomerAnnotations } from "@/modules/contacts/customers/useCustomerAnnotations";
import { RejectionNotice } from "@/modules/contacts/screens/RejectionNotice";

/**
 * The team's note on a customer (#447).
 *
 * ERP-only: it is stored beside the mirrored customer and never sent to
 * Shopify, which is why — unlike the order note — saving takes no guard and
 * can raise no conflict. A refusal keeps the textarea open with what was
 * typed, and says why.
 */
export function CustomerNoteEditor({
  customerId,
  note,
  canEdit,
}: {
  customerId: string;
  note: CustomerNote | null;
  canEdit: boolean;
}) {
  const data = useDataSource();
  const annotations = useCustomerAnnotations(customerId);
  const [draft, setDraft] = useState<string | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (body: string) => data.customers.setNote(customerId, body),
    onSuccess: (result) => {
      if (result.ok) {
        setDraft(null);
        setRejection(null);
        annotations.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (draft === null) {
    return (
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {note ? (
              <p className="break-words whitespace-pre-line">{note.body}</p>
            ) : (
              <p className="text-muted-foreground">No note.</p>
            )}
          </div>
          {canEdit ? (
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label={note ? "Edit note" : "Add a note"}
              onClick={() => {
                setRejection(null);
                setDraft(note?.body ?? "");
              }}
            >
              {note ? <Pencil /> : <Plus />}
            </Button>
          ) : null}
        </div>
        {note ? (
          <p className="text-muted-foreground text-xs">
            {note.updatedBy ? `${note.updatedBy}, ` : ""}
            {dateTime(note.updatedAt)}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        autoFocus
        rows={4}
        aria-label="Customer note"
        value={draft}
        disabled={mutation.isPending}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={mutation.isPending}
          onClick={() => {
            setRejection(null);
            mutation.mutate(draft);
          }}
        >
          {mutation.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            setDraft(null);
            setRejection(null);
          }}
        >
          Cancel
        </Button>
      </div>
      {rejection ? <RejectionNotice rejection={rejection} /> : null}
    </div>
  );
}

/**
 * The customer's ERP tags, from the shared vocabulary (#447).
 *
 * Adding a tag the vocabulary already holds, in any case, uses the existing
 * one; removing a tag takes it off this customer only. Neither reaches
 * Shopify — the tags Shopify holds on the customer are shown apart, read-only.
 */
export function CustomerTagsEditor({
  customerId,
  tags,
  canEdit,
}: {
  customerId: string;
  tags: string[];
  canEdit: boolean;
}) {
  const data = useDataSource();
  const annotations = useCustomerAnnotations(customerId);
  const [adding, setAdding] = useState<string | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (input: { tag: string; mode: "add" | "remove" }) =>
      input.mode === "add"
        ? data.customers.addTag(customerId, input.tag)
        : data.customers.removeTag(customerId, input.tag),
    onSuccess: (result, input) => {
      if (result.ok) {
        if (input.mode === "add") setAdding(null);
        setRejection(null);
        annotations.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  function submit() {
    if (adding === null || adding.trim() === "") return;
    setRejection(null);
    mutation.mutate({ tag: adding, mode: "add" });
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className={canEdit ? "gap-1 py-1 pr-1 font-normal" : "font-normal"}
          >
            {tag}
            {canEdit ? (
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                disabled={mutation.isPending}
                onClick={() => {
                  setRejection(null);
                  mutation.mutate({ tag, mode: "remove" });
                }}
                className="hover:bg-secondary-foreground/10 rounded-full p-0.5 disabled:pointer-events-none disabled:opacity-50"
              >
                <X className="size-3" />
              </button>
            ) : null}
          </Badge>
        ))}

        {tags.length === 0 && adding === null ? (
          <p className="text-muted-foreground">No tags.</p>
        ) : null}

        {canEdit && adding === null ? (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            aria-label="Add a tag"
            onClick={() => {
              setRejection(null);
              setAdding("");
            }}
          >
            <Plus />
            Add
          </Button>
        ) : null}
      </div>

      {adding !== null ? (
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={adding}
            placeholder="New tag"
            aria-label="New tag"
            className="h-8 min-w-0 flex-1"
            disabled={mutation.isPending}
            onChange={(event) => {
              setAdding(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
              if (event.key === "Escape") {
                setAdding(null);
                setRejection(null);
              }
            }}
          />
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Save tag"
            disabled={adding.trim() === "" || mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Check />
            )}
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Cancel"
            disabled={mutation.isPending}
            onClick={() => {
              setAdding(null);
              setRejection(null);
            }}
          >
            <X />
          </Button>
        </div>
      ) : null}

      {rejection ? <RejectionNotice rejection={rejection} /> : null}
    </div>
  );
}
